import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Phone, Mail, MapPin, Building2, Trash2, Save, Edit3, MessageSquare, Star, Globe, Tag, Award, Clock, ExternalLink, Calendar, MapPinned, Sparkles, CheckCircle2, XCircle, AlertCircle } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { ServiceProvider, ServiceRequest } from './types';
import { SUPABASE_URL } from '../../lib/constants';

const ServiceProviderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [enrichmentJob, setEnrichmentJob] = useState<any>(null);
  const [startingEnrichment, setStartingEnrichment] = useState(false);
  
  const [editFormData, setEditFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    website: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    primary_category: '',
    internal_rating: null as number | null,
    preferred_provider: false,
    status: 'active' as 'active' | 'inactive' | 'blacklisted' | 'pending_verification',
    coverage_type: null as 'radius' | 'cities' | 'states' | 'counties' | 'zip_codes' | 'unlimited' | null,
    service_radius_miles: null as number | null,
    coverage_cities: [] as string[],
    coverage_states: [] as string[],
    coverage_counties: [] as string[],
    coverage_zip_codes: [] as string[],
    can_contact_via_call: 'unknown' as 'yes' | 'no' | 'unknown',
    can_contact_via_text: 'unknown' as 'yes' | 'no' | 'unknown',
    can_contact_via_email: 'unknown' as 'yes' | 'no' | 'unknown'
  });

  useEffect(() => {
    if (!id) {
      navigate('/admin?tab=providers');
      return;
    }
    fetchProvider();
    fetchServiceRequests();
    fetchEnrichmentJob();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    // Set up realtime subscription for enrichment job updates
    const channel = supabase
      .channel(`enrichment-job-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'provider_enrichment_jobs',
          filter: `service_provider_id=eq.${id}`
        },
        (payload) => {
          console.log('Enrichment job update:', payload);
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            setEnrichmentJob(payload.new);
            // Refresh provider data if job completed
            if (payload.new.status === 'completed') {
              fetchProvider();
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  const fetchProvider = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setProvider(data);
      setEditFormData({
        company_name: data.company_name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        notes: data.notes || '',
        website: data.website || '',
        street_address: data.street_address || '',
        city: data.city || '',
        state: data.state || '',
        postal_code: data.postal_code || '',
        primary_category: data.primary_category || '',
        internal_rating: data.internal_rating,
        preferred_provider: data.preferred_provider || false,
        status: data.status || 'active',
        coverage_type: data.coverage_type || null,
        service_radius_miles: data.service_radius_miles || null,
        coverage_cities: data.coverage_cities || [],
        coverage_states: data.coverage_states || [],
        coverage_counties: data.coverage_counties || [],
        coverage_zip_codes: data.coverage_zip_codes || [],
        can_contact_via_call: data.can_contact_via_call || 'unknown',
        can_contact_via_text: data.can_contact_via_text || 'unknown',
        can_contact_via_email: data.can_contact_via_email || 'unknown'
      });
    } catch (error: any) {
      console.error('Error fetching service provider:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrichmentJob = async () => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase
        .from('provider_enrichment_jobs')
        .select('*')
        .eq('service_provider_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching enrichment job:', error);
      } else if (data) {
        setEnrichmentJob(data);
      }
    } catch (error: any) {
      console.error('Error fetching enrichment job:', error);
    }
  };

  const handleStartEnrichment = async () => {
    if (!id || !provider?.website) return;

    try {
      setStartingEnrichment(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/enrich-service-provider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ service_provider_id: id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start enrichment');
      }

      const result = await response.json();
      // Fetch the job to update UI
      await fetchEnrichmentJob();
    } catch (error: any) {
      console.error('Error starting enrichment:', error);
      alert(`Failed to start enrichment: ${error.message}`);
    } finally {
      setStartingEnrichment(false);
    }
  };

  const fetchServiceRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          description,
          priority,
          status,
          created_at,
          home:homes (
            address,
            city,
            state
          )
        `)
        .eq('service_provider_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServiceRequests(data || []);
    } catch (error) {
      console.error('Error fetching service requests:', error);
    }
  };

  const handleSave = async () => {
    if (!provider) return;

    setSaving(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('service_providers')
        .update({
          company_name: editFormData.company_name,
          email: editFormData.email || null,
          phone: editFormData.phone || null,
          address: editFormData.address || null,
          notes: editFormData.notes || null,
          website: editFormData.website || null,
          street_address: editFormData.street_address || null,
          city: editFormData.city || null,
          state: editFormData.state || null,
          postal_code: editFormData.postal_code || null,
          primary_category: editFormData.primary_category || null,
          internal_rating: editFormData.internal_rating,
          preferred_provider: editFormData.preferred_provider,
          status: editFormData.status,
          coverage_type: editFormData.coverage_type || null,
          service_radius_miles: editFormData.service_radius_miles || null,
          coverage_cities: editFormData.coverage_cities.length > 0 ? editFormData.coverage_cities : null,
          coverage_states: editFormData.coverage_states.length > 0 ? editFormData.coverage_states : null,
          coverage_counties: editFormData.coverage_counties.length > 0 ? editFormData.coverage_counties : null,
          coverage_zip_codes: editFormData.coverage_zip_codes.length > 0 ? editFormData.coverage_zip_codes : null,
          can_contact_via_call: editFormData.can_contact_via_call,
          can_contact_via_text: editFormData.can_contact_via_text,
          can_contact_via_email: editFormData.can_contact_via_email
        })
        .eq('id', provider.id);

      if (error) throw error;

      // Refresh provider data
      await fetchProvider();
      setEditing(false);
    } catch (error: any) {
      console.error('Error updating service provider:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!provider) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('service_providers')
        .delete()
        .eq('id', provider.id);

      if (error) throw error;

      navigate('/admin?tab=providers');
    } catch (error: any) {
      console.error('Error deleting service provider:', error);
      setError(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: ServiceRequest['status']) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'cancelled': return 'text-gray-600 bg-gray-50';
      default: return 'text-orange-600 bg-orange-50';
    }
  };

  const getPriorityColor = (priority: ServiceRequest['priority']) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'normal': return 'text-blue-600 bg-blue-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleArrayInputChange = (
    field: 'coverage_cities' | 'coverage_states' | 'coverage_counties' | 'coverage_zip_codes',
    value: string
  ) => {
    // Split by comma or newline, trim, and filter out empty strings
    const items = value
      .split(/[,\n]/)
      .map(item => item.trim())
      .filter(item => item.length > 0);
    setEditFormData({ ...editFormData, [field]: items });
  };

  const getArrayInputValue = (field: 'coverage_cities' | 'coverage_states' | 'coverage_counties' | 'coverage_zip_codes') => {
    return editFormData[field].join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {error || 'Service provider not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/admin?tab=providers')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          Back to Providers
        </button>
        <h1 className="text-2xl font-bold">Service Provider Details</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Provider Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center space-x-3">
                <Building2 className="w-6 h-6 text-gray-400" />
                <h2 className="text-xl font-semibold">Provider Information</h2>
              </div>
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center space-x-2 px-3 py-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditing(false);
                      setEditFormData({
                        company_name: provider.company_name || '',
                        email: provider.email || '',
                        phone: provider.phone || '',
                        address: provider.address || '',
                        notes: provider.notes || '',
                        website: provider.website || '',
                        street_address: provider.street_address || '',
                        city: provider.city || '',
                        state: provider.state || '',
                        postal_code: provider.postal_code || '',
                        primary_category: provider.primary_category || '',
                        internal_rating: provider.internal_rating,
                        preferred_provider: provider.preferred_provider || false,
                        status: provider.status || 'active',
                        coverage_type: provider.coverage_type || null,
                        service_radius_miles: provider.service_radius_miles || null,
                        coverage_cities: provider.coverage_cities || [],
                        coverage_states: provider.coverage_states || [],
                        coverage_counties: provider.coverage_counties || [],
                        coverage_zip_codes: provider.coverage_zip_codes || []
                      });
                    }}
                    className="px-3 py-1.5 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={editFormData.company_name}
                    onChange={(e) => setEditFormData({ ...editFormData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  />
                ) : (
                  <p className="text-gray-900">{provider.company_name}</p>
                )}
              </div>

              {/* Primary Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Tag className="w-4 h-4" />
                    <span>Primary Category</span>
                  </div>
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={editFormData.primary_category}
                    onChange={(e) => setEditFormData({ ...editFormData, primary_category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    placeholder="e.g., Plumbing, HVAC, Electrical"
                  />
                ) : (
                  <p className="text-gray-900">{provider.primary_category || 'Not provided'}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </div>
                </label>
                {editing ? (
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    placeholder="contact@company.com"
                  />
                ) : (
                  <p className="text-gray-900">{provider.email || 'Not provided'}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>Phone</span>
                  </div>
                </label>
                {editing ? (
                  <input
                    type="tel"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    placeholder="(555) 123-4567"
                  />
                ) : (
                  <p className="text-gray-900">{provider.phone || 'Not provided'}</p>
                )}
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4" />
                    <span>Website</span>
                  </div>
                </label>
                {editing ? (
                  <input
                    type="url"
                    value={editFormData.website}
                    onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    placeholder="https://example.com"
                  />
                ) : (
                  provider.website ? (
                    <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center space-x-1">
                      <span>{provider.website}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <p className="text-gray-900">Not provided</p>
                  )
                )}
              </div>
            </div>

            {/* Contact Preferences Section */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="flex items-center space-x-2 mb-4">
                <MessageSquare className="w-5 h-5 text-gray-700" />
                <h3 className="text-lg font-medium text-gray-900">Contact Preferences</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Does this provider accept requests for service/estimates via these methods?
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Can Contact Via Call */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4" />
                      <span>Accept Phone Calls</span>
                    </div>
                  </label>
                  {editing ? (
                    <select
                      value={editFormData.can_contact_via_call}
                      onChange={(e) => setEditFormData({ ...editFormData, can_contact_via_call: e.target.value as 'yes' | 'no' | 'unknown' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="unknown">Unknown</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : (
                    <p className={`text-sm font-medium ${
                      provider.can_contact_via_call === 'yes' ? 'text-green-600' : 
                      provider.can_contact_via_call === 'no' ? 'text-red-600' : 
                      'text-gray-500'
                    }`}>
                      {provider.can_contact_via_call === 'yes' ? '✓ Yes' : 
                       provider.can_contact_via_call === 'no' ? '✗ No' : 
                       '? Unknown'}
                    </p>
                  )}
                </div>

                {/* Can Contact Via Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4" />
                      <span>Accept Text/SMS</span>
                    </div>
                  </label>
                  {editing ? (
                    <select
                      value={editFormData.can_contact_via_text}
                      onChange={(e) => setEditFormData({ ...editFormData, can_contact_via_text: e.target.value as 'yes' | 'no' | 'unknown' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="unknown">Unknown</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : (
                    <p className={`text-sm font-medium ${
                      provider.can_contact_via_text === 'yes' ? 'text-green-600' : 
                      provider.can_contact_via_text === 'no' ? 'text-red-600' : 
                      'text-gray-500'
                    }`}>
                      {provider.can_contact_via_text === 'yes' ? '✓ Yes' : 
                       provider.can_contact_via_text === 'no' ? '✗ No' : 
                       '? Unknown'}
                    </p>
                  )}
                </div>

                {/* Can Contact Via Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <span>Accept Email</span>
                    </div>
                  </label>
                  {editing ? (
                    <select
                      value={editFormData.can_contact_via_email}
                      onChange={(e) => setEditFormData({ ...editFormData, can_contact_via_email: e.target.value as 'yes' | 'no' | 'unknown' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="unknown">Unknown</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  ) : (
                    <p className={`text-sm font-medium ${
                      provider.can_contact_via_email === 'yes' ? 'text-green-600' : 
                      provider.can_contact_via_email === 'no' ? 'text-red-600' : 
                      'text-gray-500'
                    }`}>
                      {provider.can_contact_via_email === 'yes' ? '✓ Yes' : 
                       provider.can_contact_via_email === 'no' ? '✗ No' : 
                       '? Unknown'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="w-5 h-5 text-gray-700" />
                <h3 className="text-lg font-medium text-gray-900">Address</h3>
              </div>

              {/* Address - Street */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={editFormData.street_address}
                    onChange={(e) => setEditFormData({ ...editFormData, street_address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    placeholder="123 Main St"
                  />
                ) : (
                  <p className="text-gray-900">{provider.street_address || provider.address || 'Not provided'}</p>
                )}
              </div>

              {/* City, State, Zip - Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                      placeholder="City"
                    />
                  ) : (
                    <p className="text-gray-900">{provider.city || '—'}</p>
                  )}
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editFormData.state}
                      onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                      placeholder="State"
                      maxLength={2}
                    />
                  ) : (
                    <p className="text-gray-900">{provider.state || '—'}</p>
                  )}
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip Code
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editFormData.postal_code}
                      onChange={(e) => setEditFormData({ ...editFormData, postal_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                      placeholder="12345"
                    />
                  ) : (
                    <p className="text-gray-900">{provider.postal_code || '—'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Coverage Areas Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3 mb-6">
              <MapPinned className="w-6 h-6 text-gray-400" />
              <h2 className="text-xl font-semibold">Coverage Areas</h2>
            </div>

            <div className="space-y-4">
              {/* Coverage Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coverage Type
                </label>
                {editing ? (
                  <select
                    value={editFormData.coverage_type || ''}
                    onChange={(e) => {
                      const newType = e.target.value || null;
                      setEditFormData({
                        ...editFormData,
                        coverage_type: newType as any,
                        // Clear fields that aren't relevant to the new type
                        service_radius_miles: newType === 'radius' ? editFormData.service_radius_miles : null,
                        coverage_cities: newType === 'cities' ? editFormData.coverage_cities : [],
                        coverage_states: newType === 'states' ? editFormData.coverage_states : [],
                        coverage_counties: newType === 'counties' ? editFormData.coverage_counties : [],
                        coverage_zip_codes: newType === 'zip_codes' ? editFormData.coverage_zip_codes : []
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    <option value="">Not set</option>
                    <option value="radius">Radius (miles from location)</option>
                    <option value="cities">Cities</option>
                    <option value="states">States</option>
                    <option value="counties">Counties</option>
                    <option value="zip_codes">Zip Codes</option>
                    <option value="unlimited">Unlimited (serves everywhere)</option>
                  </select>
                ) : (
                  <p className="text-gray-900">
                    {provider.coverage_type 
                      ? provider.coverage_type.charAt(0).toUpperCase() + provider.coverage_type.slice(1).replace('_', ' ')
                      : 'Not set'}
                  </p>
                )}
              </div>

              {/* Radius Input */}
              {(editing ? editFormData.coverage_type === 'radius' : provider.coverage_type === 'radius') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Radius (miles)
                  </label>
                  {editing ? (
                    <input
                      type="number"
                      min="1"
                      value={editFormData.service_radius_miles || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        service_radius_miles: e.target.value ? parseInt(e.target.value) : null
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                      placeholder="e.g., 25"
                    />
                  ) : (
                    <p className="text-gray-900">
                      {provider.service_radius_miles 
                        ? `${provider.service_radius_miles} miles`
                        : 'Not set'}
                    </p>
                  )}
                </div>
              )}

              {/* Cities Input */}
              {(editing ? editFormData.coverage_type === 'cities' : provider.coverage_type === 'cities') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coverage Cities
                    <span className="text-gray-500 text-xs ml-2">(comma or newline separated)</span>
                  </label>
                  {editing ? (
                    <textarea
                      value={getArrayInputValue('coverage_cities')}
                      onChange={(e) => handleArrayInputChange('coverage_cities', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black resize-none"
                      placeholder="e.g., San Francisco, Los Angeles, San Diego"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {provider.coverage_cities && provider.coverage_cities.length > 0 ? (
                        provider.coverage_cities.map((city, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                            {city}
                          </span>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No cities specified</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* States Input */}
              {(editing ? editFormData.coverage_type === 'states' : provider.coverage_type === 'states') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coverage States
                    <span className="text-gray-500 text-xs ml-2">(comma or newline separated, e.g., CA, NY, TX)</span>
                  </label>
                  {editing ? (
                    <textarea
                      value={getArrayInputValue('coverage_states')}
                      onChange={(e) => handleArrayInputChange('coverage_states', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black resize-none"
                      placeholder="e.g., CA, NY, TX"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {provider.coverage_states && provider.coverage_states.length > 0 ? (
                        provider.coverage_states.map((state, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                            {state}
                          </span>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No states specified</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Counties Input */}
              {(editing ? editFormData.coverage_type === 'counties' : provider.coverage_type === 'counties') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coverage Counties
                    <span className="text-gray-500 text-xs ml-2">(comma or newline separated)</span>
                  </label>
                  {editing ? (
                    <textarea
                      value={getArrayInputValue('coverage_counties')}
                      onChange={(e) => handleArrayInputChange('coverage_counties', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black resize-none"
                      placeholder="e.g., Los Angeles County, Orange County, San Diego County"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {provider.coverage_counties && provider.coverage_counties.length > 0 ? (
                        provider.coverage_counties.map((county, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                            {county}
                          </span>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No counties specified</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Zip Codes Input */}
              {(editing ? editFormData.coverage_type === 'zip_codes' : provider.coverage_type === 'zip_codes') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coverage Zip Codes
                    <span className="text-gray-500 text-xs ml-2">(comma or newline separated)</span>
                  </label>
                  {editing ? (
                    <textarea
                      value={getArrayInputValue('coverage_zip_codes')}
                      onChange={(e) => handleArrayInputChange('coverage_zip_codes', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black resize-none"
                      placeholder="e.g., 94102, 94103, 94104"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {provider.coverage_zip_codes && provider.coverage_zip_codes.length > 0 ? (
                        provider.coverage_zip_codes.map((zip, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800">
                            {zip}
                          </span>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No zip codes specified</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Unlimited Coverage Display */}
              {(editing ? editFormData.coverage_type === 'unlimited' : provider.coverage_type === 'unlimited') && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <p className="text-sm text-green-800 font-medium">
                    This provider serves all locations (unlimited coverage)
                  </p>
                </div>
              )}

              {/* Display current coverage summary when not editing */}
              {!editing && provider.coverage_type && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    {provider.coverage_type === 'unlimited' && (
                      <>Covers all locations (unlimited coverage)</>
                    )}
                    {provider.coverage_type === 'radius' && provider.service_radius_miles && (
                      <>Covers locations within {provider.service_radius_miles} miles of provider location</>
                    )}
                    {provider.coverage_type === 'cities' && provider.coverage_cities && provider.coverage_cities.length > 0 && (
                      <>Covers {provider.coverage_cities.length} cit{provider.coverage_cities.length === 1 ? 'y' : 'ies'}</>
                    )}
                    {provider.coverage_type === 'states' && provider.coverage_states && provider.coverage_states.length > 0 && (
                      <>Covers {provider.coverage_states.length} state{provider.coverage_states.length === 1 ? '' : 's'}</>
                    )}
                    {provider.coverage_type === 'counties' && provider.coverage_counties && provider.coverage_counties.length > 0 && (
                      <>Covers {provider.coverage_counties.length} count{provider.coverage_counties.length === 1 ? 'y' : 'ies'}</>
                    )}
                    {provider.coverage_type === 'zip_codes' && provider.coverage_zip_codes && provider.coverage_zip_codes.length > 0 && (
                      <>Covers {provider.coverage_zip_codes.length} zip code{provider.coverage_zip_codes.length === 1 ? '' : 's'}</>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Ratings & Reviews Section */}
          {(provider.rating || provider.reviews_count || editing) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-500" />
                Ratings & Reviews
              </h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rating
                  </label>
                  {provider.rating ? (
                    <div className="flex items-center space-x-2">
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      <span className="text-xl font-semibold">{provider.rating.toFixed(1)}</span>
                      <span className="text-gray-500 text-sm">/ 5.0</span>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No rating yet</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Count
                  </label>
                  <p className="text-xl font-semibold">{provider.reviews_count || 0}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Images
                  </label>
                  <p className="text-xl font-semibold">{provider.images_count || 0}</p>
                </div>
              </div>

              {provider.last_review_sync_at && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Last synced: {new Date(provider.last_review_sync_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Google Places Integration */}
          {provider.google_place_id && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                <MapPinned className="w-5 h-5 mr-2 text-blue-500" />
                Google Places Integration
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Place ID
                  </label>
                  <p className="text-sm text-gray-900 font-mono">{provider.google_place_id}</p>
                </div>

                {provider.google_maps_url && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Google Maps
                    </label>
                    <a 
                      href={provider.google_maps_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 hover:underline flex items-center space-x-1 text-sm"
                    >
                      <span>View on Google Maps</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-2">
                  {provider.is_claimed !== null && (
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs ${provider.is_claimed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {provider.is_claimed ? 'Claimed' : 'Unclaimed'}
                      </span>
                    </div>
                  )}
                  {provider.is_permanently_closed && (
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                        Permanently Closed
                      </span>
                    </div>
                  )}
                  {provider.is_temporarily_closed && (
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">
                        Temporarily Closed
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Business Hours */}
          {provider.opening_hours && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-green-500" />
                Business Hours
              </h3>
              
              {provider.hours_summary ? (
                <p className="text-sm text-gray-700">{provider.hours_summary}</p>
              ) : (
                <div className="text-sm text-gray-500">Hours information available</div>
              )}
              
              {provider.is_24_hours && (
                <div className="mt-3">
                  <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                    Open 24 Hours
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Categories & Services */}
          {(provider.categories && provider.categories.length > 0) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                <Tag className="w-5 h-5 mr-2 text-purple-500" />
                Categories & Services
              </h3>
              
              <div className="flex flex-wrap gap-2">
                {provider.categories.map((category, index) => (
                  <span key={index} className="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Internal Management */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2 text-indigo-500" />
              Internal Management
            </h3>
            
            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                {editing ? (
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending_verification">Pending Verification</option>
                    <option value="blacklisted">Blacklisted</option>
                  </select>
                ) : (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    provider.status === 'active' ? 'bg-green-100 text-green-800' :
                    provider.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-800' :
                    provider.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {provider.status ? provider.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Active'}
                  </span>
                )}
              </div>

              {/* Internal Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Rating (1-5)
                </label>
                {editing ? (
                  <select
                    value={editFormData.internal_rating || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, internal_rating: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    <option value="">Not rated</option>
                    <option value="1">1 - Poor</option>
                    <option value="2">2 - Fair</option>
                    <option value="3">3 - Good</option>
                    <option value="4">4 - Very Good</option>
                    <option value="5">5 - Excellent</option>
                  </select>
                ) : (
                  <div className="flex items-center space-x-2">
                    {provider.internal_rating ? (
                      <>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${
                              i < provider.internal_rating! 
                                ? 'text-yellow-500 fill-yellow-500' 
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </>
                    ) : (
                      <span className="text-gray-500">Not rated</span>
                    )}
                  </div>
                )}
              </div>

              {/* Preferred Provider */}
              <div>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing ? editFormData.preferred_provider : provider.preferred_provider || false}
                    onChange={(e) => editing && setEditFormData({ ...editFormData, preferred_provider: e.target.checked })}
                    disabled={!editing}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-medium text-gray-700">Preferred Provider</span>
                  </div>
                </label>
              </div>

              {/* Verification */}
              {provider.verified_at && (
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Verified on {new Date(provider.verified_at).toLocaleDateString()}</span>
                    {provider.verification_method && (
                      <span className="text-gray-400">via {provider.verification_method}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-gray-500" />
              Notes
            </h3>
            
            {editing ? (
              <textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black resize-none"
                placeholder="Add notes about this service provider..."
              />
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg min-h-[100px]">
                {provider.notes ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{provider.notes}</p>
                ) : (
                  <p className="text-gray-400 italic">No notes yet</p>
                )}
              </div>
            )}
          </div>

          {/* Associated Service Requests */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-medium text-gray-900 mb-4">
              Associated Service Requests ({serviceRequests.length})
            </h3>
            
            {serviceRequests.length === 0 ? (
              <p className="text-gray-500 text-sm italic">No service requests assigned to this provider yet</p>
            ) : (
              <div className="space-y-3">
                {serviceRequests.map((request) => (
                  <div
                    key={request.id}
                    onClick={() => navigate(`/admin/service-request/${request.id}`)}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-sm">{request.title}</h4>
                        {request.home && (
                          <p className="text-xs text-gray-500 mt-1">
                            {request.home.address}, {request.home.city}, {request.home.state}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-md text-xs ${getPriorityColor(request.priority)}`}>
                          {request.priority}
                        </span>
                        <span className={`px-2 py-1 rounded-md text-xs ${getStatusColor(request.status)}`}>
                          {request.status.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full flex items-center space-x-2 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Provider</span>
              </button>
            </div>
          </div>

          {/* Enrichment Section */}
          {provider && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-gray-900">Website Enrichment</h3>
                </div>
              </div>

              {provider.website && (
                (!enrichmentJob || enrichmentJob.status === 'completed' || enrichmentJob.status === 'failed') && (
                  <button
                    onClick={handleStartEnrichment}
                    disabled={startingEnrichment}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 !bg-blue-600 !text-white rounded-md hover:!bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium mb-4"
                    style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                  >
                    {startingEnrichment ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span className="text-white">Starting...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 text-white" />
                        <span className="text-white">
                          {enrichmentJob ? 'Enrich Again' : 'Start Enrichment'}
                        </span>
                      </>
                    )}
                  </button>
                )
              )}

              {enrichmentJob ? (
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center space-x-2">
                    {enrichmentJob.status === 'completed' && (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    )}
                    {enrichmentJob.status === 'failed' && (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    {(enrichmentJob.status === 'pending' || enrichmentJob.status === 'crawling' || enrichmentJob.status === 'processing') && (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    )}
                    <span className={`text-sm font-medium ${
                      enrichmentJob.status === 'completed' ? 'text-green-600' :
                      enrichmentJob.status === 'failed' ? 'text-red-600' :
                      'text-blue-600'
                    }`}>
                      {enrichmentJob.status.charAt(0).toUpperCase() + enrichmentJob.status.slice(1)}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  {(enrichmentJob.status === 'pending' || enrichmentJob.status === 'crawling' || enrichmentJob.status === 'processing') && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{enrichmentJob.current_step || 'Processing...'}</span>
                        <span className="text-gray-600">{enrichmentJob.progress_percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${enrichmentJob.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {enrichmentJob.status === 'failed' && enrichmentJob.error_message && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-800">Enrichment Failed</p>
                          <p className="text-sm text-red-700 mt-1">{enrichmentJob.error_message}</p>
                        </div>
                      </div>
                      {provider.website && (
                        <button
                          onClick={handleStartEnrichment}
                          disabled={startingEnrichment}
                          className="mt-3 w-full flex items-center justify-center space-x-2 px-3 py-1.5 text-sm !bg-blue-600 !text-white rounded-md hover:!bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                          style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                        >
                          {startingEnrichment ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin text-white" />
                              <span className="text-white">Starting...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3 text-white" />
                              <span className="text-white">Retry</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Success Message */}
                  {enrichmentJob.status === 'completed' && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800">Enrichment Completed</p>
                          <p className="text-sm text-green-700 mt-1">
                            Provider data has been updated with information from the website.
                          </p>
                          {enrichmentJob.completed_at && (
                            <p className="text-xs text-green-600 mt-1">
                              Completed at {new Date(enrichmentJob.completed_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      {provider.website && (
                        <button
                          onClick={handleStartEnrichment}
                          disabled={startingEnrichment}
                          className="mt-3 w-full flex items-center justify-center space-x-2 px-3 py-1.5 text-sm !bg-blue-600 !text-white rounded-md hover:!bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                          style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                        >
                          {startingEnrichment ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin text-white" />
                              <span className="text-white">Starting...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3 text-white" />
                              <span className="text-white">Enrich Again</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Enrichment Metadata */}
                  {provider.enrichment_metadata && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Enrichment Details</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        {provider.enrichment_metadata.has_contact_form && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span>Contact form detected</span>
                            {provider.enrichment_metadata.contact_form_url && (
                              <a
                                href={provider.enrichment_metadata.contact_form_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View form
                              </a>
                            )}
                          </div>
                        )}
                        {provider.enrichment_metadata.last_enriched_at && (
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span>
                              Last enriched: {new Date(provider.enrichment_metadata.last_enriched_at).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : provider.website ? (
                <p className="text-sm text-gray-600">
                  No enrichment job found. Click "Start Enrichment" to begin extracting information from the provider's website.
                </p>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  This provider does not have a website URL configured.
                </p>
              )}
            </div>
          )}

          {/* Data Quality Score */}
          {provider.data_quality_score !== null && provider.data_quality_score !== undefined && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-medium text-gray-900 mb-4">Data Quality</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Completeness</span>
                    <span className={`text-lg font-semibold ${
                      provider.data_quality_score >= 0.8 ? 'text-green-600' :
                      provider.data_quality_score >= 0.5 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {Math.round(provider.data_quality_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        provider.data_quality_score >= 0.8 ? 'bg-green-500' :
                        provider.data_quality_score >= 0.5 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${Math.round(provider.data_quality_score * 100)}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Based on completeness of contact info, location, ratings, and other data
                </p>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-medium text-gray-900 mb-4">Metadata</h3>
            <div className="space-y-3 text-sm">
              {provider.data_source && (
                <div>
                  <span className="text-gray-500 block mb-1">Data Source:</span>
                  <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                    {provider.data_source}
                  </span>
                </div>
              )}
              
              {provider.sync_status && (
                <div>
                  <span className="text-gray-500 block mb-1">Sync Status:</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    provider.sync_status === 'active' ? 'bg-green-100 text-green-800' :
                    provider.sync_status === 'stale' ? 'bg-yellow-100 text-yellow-800' :
                    provider.sync_status === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {provider.sync_status}
                  </span>
                </div>
              )}

              {provider.last_synced_at && (
                <div>
                  <span className="text-gray-500 block">Last Synced:</span>
                  <p className="text-gray-900">{new Date(provider.last_synced_at).toLocaleString()}</p>
                </div>
              )}

              {provider.discovered_at && (
                <div>
                  <span className="text-gray-500 block">Discovered:</span>
                  <p className="text-gray-900">{new Date(provider.discovered_at).toLocaleString()}</p>
                </div>
              )}
              
              <div className="pt-3 border-t border-gray-200">
                <span className="text-gray-500 block">Created:</span>
                <p className="text-gray-900">{new Date(provider.created_at).toLocaleString()}</p>
              </div>
              <div>
                <span className="text-gray-500 block">Last Updated:</span>
                <p className="text-gray-900">{new Date(provider.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-red-600">Delete Service Provider</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Trash2 className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Are you sure you want to delete this service provider? This action cannot be undone.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="font-medium text-sm">{provider.company_name}</p>
                    {serviceRequests.length > 0 && (
                      <p className="text-xs text-orange-600 mt-1">
                        Warning: This provider has {serviceRequests.length} associated service request(s)
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center justify-center space-x-2 flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Provider</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceProviderDetail;

